import { ReactElement, useRef } from 'react';
import { AnswerSource } from 'src/redux/survey/survey.types';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import barChart from '../../../assets/icons-redesign/bar-chart.svg';
import lineChart from '../../../assets/icons-redesign/line-chart.svg';

interface ChartTypeToggleProps {
  isLineChart: boolean;
  onToggle: (isLineChart: boolean) => void;
  answerFilter: AnswerSource;
}

const SLIDER_COLORS: Record<AnswerSource, string> = {
  [AnswerSource.All]: '#3358fc',
  [AnswerSource.AI]: 'rgba(117, 35, 241, 1)',
  [AnswerSource.Human]: 'rgba(255, 109, 45, 1)',
  [AnswerSource.RTR]: '#3358fc',
};

const BAR_PCT = 1/2;
const LINE_PCT = 1/2;

const ChartTypeToggle = ({ isLineChart, onToggle, answerFilter }: ChartTypeToggleProps): ReactElement => {
  const thumbRef = useRef<HTMLDivElement | null>(null);
  const color = SLIDER_COLORS[answerFilter] || '#3358fc';
  const containerWidth = 96;
  const barWidth = containerWidth * BAR_PCT;
  const lineWidth = containerWidth * LINE_PCT;
  const padding = 4;

  useGSAP(() => {
    const width = isLineChart ? lineWidth - 2 * padding : barWidth - 2 * padding;
    const left = isLineChart ? barWidth + padding : padding;
    gsap.to(thumbRef.current, {
      backgroundColor: color,
      width: width,
      left: left,
      duration: 0.25,
    });
  }, [isLineChart, answerFilter, barWidth, lineWidth]);

  return (
    <div className="relative flex w-24 h-10 items-center bg-white/20 rounded-full select-none">
      {/* Sliding thumb */}
      <div
        ref={thumbRef}
        className="absolute top-1 h-8 rounded-full z-0 transition-colors"
        style={{ pointerEvents: 'none', left: padding, width: barWidth - 2 * padding }}
      />
      {/* Options */}
      <div className="relative flex w-full h-full z-10">
        <button
          className={`flex items-center justify-center font-medium text-xs transition-colors rounded-full h-full ${!isLineChart ? 'text-white' : 'text-gray-300'}`}
          style={{ flexBasis: `${BAR_PCT * 100}%`, zIndex: 1 }}
          onClick={() => onToggle(false)}
          type="button"
        >
          <img
            src={barChart}
            alt="Bar Chart"
            style={{
              filter: !isLineChart ? 'none' : 'brightness(0.5)',
              width: '16px',
              height: '16px',
            }}
          />
        </button>
        <button
          className={`flex items-center justify-center font-medium text-xs transition-colors rounded-full h-full ${isLineChart ? 'text-white' : 'text-gray-300'}`}
          style={{ flexBasis: `${LINE_PCT * 100}%`, zIndex: 1 }}
          onClick={() => onToggle(true)}
          type="button"
        >
          <img
            src={lineChart}
            alt="Line Chart"
            style={{
              filter: isLineChart ? 'none' : 'brightness(0.5)',
              width: '22px',
              height: '22px',
            }}
          />
        </button>
      </div>
    </div>
  );
};

export default ChartTypeToggle;